import { Table as MuiTable, SxProps } from "@mui/material";
import {
  ColumnDef,
  getCoreRowModel,
  getSortedRowModel,
  Row,
  RowSelectionState,
  SortingState,
  Updater,
  useReactTable,
} from "@tanstack/react-table";
import {
  forwardRef,
  memo,
  Ref,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useClearSelectionWhenServiceCenterChanges } from "../../../hooks/useClearSelectionWhenServiceCenterChanges";
import { useTableSelectionParam } from "../../../hooks/useTableSelectionParam";
import { ContextMenuAction } from "../../../types/contextMenuAction";
import { Checkbox } from "../Checkbox";
import type { DraggedItem } from "../dragndrop/types";
import Loading from "../layout/Loading";
import TableCaption from "../TableCaption";
import { DroppableContainer } from "./DroppableContainer";
import { SelectableProps } from "./Selectable.types";
import TableBody from "./TableBody";
import TableHeader from "./TableHeader";

type CommonProps<TData> = {
  /** table reference */
  ref?: Ref<TableRef>;
  /** table data */
  data?: TData[];
  /** columns definition array */
  columns: ColumnDef<TData, any>[];
  /** columns before the definition */
  preColumns?: ColumnDef<TData, any>[];
  /** Table caption for a11y and testing purposes. The caption is visually hidden. */
  caption: string;
  /** display a loading indicator instead of the table when true */
  isLoading?: boolean;
  /** custom styles for Table Container */
  styles?: SxProps;
  /** function to get the checkbox aria-label for a row (defaults to "Trailer {row.id}") */
  getCheckboxLabel?: (row: Row<TData>) => string;
};

type DragNDropProps<TData> =
  | {
      /** Droppable table data */
      droppableTableData: (row: Row<TData>) => DraggedItem;
      /** Droppable table data if it is empty */
      emptyDroppableTableData?: DraggedItem;
      /** Droppable row data */
      droppableRowData?: never;
      /** can table sort/reorder rows */
      isSortable?: boolean;
    }
  | {
      /** Droppable table data */
      droppableTableData?: never;
      /** Droppable table data if it is empty */
      emptyDroppableTableData?: never;
      /** Droppable row data */
      droppableRowData: (row: Row<TData>) => DraggedItem;
      /** can table sort/reorder rows */
      isSortable?: never;
    }
  | {
      /** Droppable table data */
      droppableTableData?: never;
      /** Droppable table data if it is empty */
      emptyDroppableTableData?: never;
      /** Droppable row data */
      droppableRowData?: never;
      /** can table sort/reorder rows */
      isSortable?: never;
    };

type ContextMenuProps<TData> = {
  /** context menu */
  contextMenuActions?: (row?: TData) => ContextMenuAction[];
};

type SortableProps<TData> = {
  /** default sort array */
  defaultSort?: SortingState;
  /** callback when sorting changes (optional, for external sort control) */
  onSortingChange?: (sorting: SortingState) => void;
  onSortedDataChange?: (rows: TData[]) => void;
  /**
   * When true, clicking an active sort column a 3rd time clears sorting
   * (default → asc → desc → default), matching MUI DataGrid behavior.
   * Defaults to false (asc ↔ desc only, no removal).
   */
  enableSortingRemoval?: boolean;
};

export type TableProps<TData> = CommonProps<TData> &
  ContextMenuProps<TData> &
  DragNDropProps<TData> &
  SelectableProps<TData> &
  SortableProps<TData> & {
    /** function to get the scroll position of TableContainer */
    onContainerScroll?: (value: number) => void;
  };

export type TableRef = {
  clearSelection: () => void;
};

export const ACTION_COLUMN_MAX_SIZE = 48;

/** Abstracts all table business logic. */
const Table = <TData,>(
  {
    data,
    columns,
    preColumns = [],
    caption,
    styles,
    droppableTableData,
    droppableRowData,
    emptyDroppableTableData,
    rowSelectionMode = "unselectable",
    enableRowSelection,
    isLoading = false,
    isSortable = false,
    contextMenuActions,
    onRowSelection,
    getRowId,
    defaultSort = [],
    onSortingChange,
    onSortedDataChange,
    enableSortingRemoval = false,
    getCheckboxLabel,
  }: TableProps<TData>,
  ref?: Ref<TableRef>
) => {
  const { selection } = useTableSelectionParam();
  const initialRender = useRef<boolean>(selection.length > 0);

  const formatInitialSelection = useMemo(() => {
    const newSelection: RowSelectionState = {};
    selection
      .filter((x) => x)
      .forEach((id) => {
        newSelection[id] = true;
      });
    return newSelection;
  }, [selection]);

  const rowSelection = formatInitialSelection;

  const [sortBy, setSortBy] = useState<SortingState>(defaultSort);

  // Handler that updates both local state and calls external callback
  const handleSortingChange = (updater: Updater<SortingState>) => {
    const newSorting =
      typeof updater === "function" ? updater(sortBy) : updater;
    setSortBy(newSorting);
    onSortingChange?.(newSorting);
  };

  const assembleColumns = useMemo(
    () => [
      ...preColumns,
      {
        id: "select",
        size: ACTION_COLUMN_MAX_SIZE,
        maxSize: ACTION_COLUMN_MAX_SIZE,
        header: ({ table }) =>
          Checkbox({
            name: "selectAllRows",
            ariaLabel: "Select all rows",
            checked: table.getIsAllRowsSelected(),
            disabled: false,
            indeterminate: table.getIsSomeRowsSelected(),
            onChange: table.getToggleAllRowsSelectedHandler(),
          }),
        cell: ({ row }) => {
          // Don't render checkbox at all if row can't be selected
          if (!row.getCanSelect()) {
            return null;
          }
          return Checkbox({
            name: "selectRow",
            ariaLabel: getCheckboxLabel
              ? getCheckboxLabel(row)
              : `Trailer ${row.id}`,
            checked: row.getIsSelected(),
            disabled: false,
            onChange: row.getToggleSelectedHandler(),
          });
        },
      },
      ...columns,
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columns, preColumns]
  );

  const table = useReactTable<TData>({
    data: data ?? [],
    columns:
      rowSelectionMode === "checkbox" ||
      rowSelectionMode === "checkbox-and-row-click"
        ? assembleColumns
        : columns,
    getCoreRowModel: getCoreRowModel(),
    state: {
      sorting: sortBy,
      rowSelection,
    },
    defaultColumn: {
      minSize: ACTION_COLUMN_MAX_SIZE,
      size: ACTION_COLUMN_MAX_SIZE,
      sortDescFirst: true,
    },
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    onSortingChange: handleSortingChange,
    getSortedRowModel: getSortedRowModel(),
    enableSortingRemoval,
    enableRowSelection:
      typeof enableRowSelection === "function"
        ? enableRowSelection
        : rowSelectionMode !== "unselectable",
    enableMultiRowSelection: rowSelectionMode !== "unselectable",
    getRowId,
    manualPagination: true,
    onRowSelectionChange: (updater) => {
      const result: RowSelectionState =
        typeof updater === "function" ? updater(rowSelection) : updater;

      onRowSelection && onRowSelection(Object.keys(result).map(String));
      initialRender.current = false;
    },
  });

  const sortedData = useMemo(() => {
    if (sortBy.length === 0) {
      return data ?? [];
    }

    return table.getSortedRowModel().rows.map((row) => row.original);
    // Disabled to prevent infinite loop since getSortedRowModel is a stable reference and we only want to recalculate when sortBy or data changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy, data, table.getState().sorting]);

  const clearSelection = () => {
    table.resetRowSelection(true);
  };

  useClearSelectionWhenServiceCenterChanges(() => clearSelection());

  useImperativeHandle(ref, () => ({
    clearSelection: () => clearSelection(),
  }));

  const previousSortedDataRef = useRef<TData[]>([]);

  useEffect(() => {
    const prev = previousSortedDataRef.current;
    const changed =
      prev.length !== sortedData.length ||
      prev.some((item, index) => item !== sortedData[index]);

    if (!changed) {
      return;
    }
    previousSortedDataRef.current = sortedData;
    onSortedDataChange?.(sortedData);
  }, [sortedData, onSortedDataChange]);

  if (isLoading) {
    return <Loading label={caption} styles={{ margin: "1rem 0" }} />;
  }

  return (
    <DroppableContainer
      styles={styles}
      emptyDroppableTableData={emptyDroppableTableData}
    >
      <MuiTable stickyHeader size="small">
        <TableCaption>{caption}</TableCaption>
        <TableHeader table={table} />
        <TableBody
          table={table}
          rowSelectionMode={rowSelectionMode}
          contextMenuActions={contextMenuActions}
          droppableTableData={droppableTableData}
          droppableRowData={droppableRowData}
          isSortable={isSortable}
        />
      </MuiTable>
    </DroppableContainer>
  );
};

Table.displayName = "Table";

export default memo(forwardRef(Table)) as <TData>(
  props: TableProps<TData>,
  ref: Ref<TableRef>
) => React.JSX.Element;
